import type { RtviCaption } from "./voiceTypes";

const RTVI_LABEL = "rtvi-ai";

export function normCaption(text: string): string {
  return text.toLowerCase().replace(/[^\w\s']/g, " ").replace(/\s+/g, " ").trim();
}

function isExtension(a: string, b: string): boolean {
  const na = normCaption(a);
  const nb = normCaption(b);
  if (!na || !nb) return false;
  return na === nb || na.startsWith(nb) || nb.startsWith(na);
}

export function sameUtterance(a: string, b: string): boolean {
  if (isExtension(a, b)) return true;
  const wa = normCaption(a).split(" ");
  const wb = normCaption(b).split(" ");
  const shared = Math.min(wa.length, wb.length, 5);
  if (shared < 3) return false;
  for (let i = 0; i < shared; i++) {
    if (wa[i] !== wb[i]) return false;
  }
  return true;
}

export function appendAssistantChunk(buf: string, chunk: string): string {
  if (!chunk) return buf;
  if (!buf) return chunk;
  if (chunk.startsWith(buf)) return chunk;
  if (buf.startsWith(chunk)) return buf;
  return `${buf.trimEnd()} ${chunk}`;
}

function pickBotSpokenText(data: Record<string, unknown>): string {
  const progress = data.spoken_progress as { accumulated_text?: string } | undefined;
  const accumulated = progress?.accumulated_text;
  if (typeof accumulated === "string" && accumulated.trim()) {
    return accumulated.trim();
  }

  const segment = String(data.text ?? "").trim();
  if (!segment) return "";

  const spoken =
    data.will_be_spoken === true ||
    data.spoken === true ||
    data.spoken_status === "in-progress" ||
    data.spoken_status === "completed";

  return spoken ? segment : "";
}

export function parseRtviMessage(raw: unknown): RtviCaption {
  if (!raw || typeof raw !== "object") return { kind: "none" };
  const msg = raw as Record<string, unknown>;
  if (String(msg.type) === "signalling" || String(msg.type) === "signaling") {
    return { kind: "none" };
  }
  if (msg.label !== RTVI_LABEL) return { kind: "none" };

  const type = String(msg.type ?? "");
  const data = (msg.data ?? {}) as Record<string, unknown>;

  switch (type) {
    case "user-transcription": {
      const text = String(data.text ?? "").trim();
      return text
        ? { kind: "user", text, final: data.final === true }
        : { kind: "none" };
    }
    case "bot-output": {
      const spoken = pickBotSpokenText(data);
      return spoken ? { kind: "bot-spoken", text: spoken } : { kind: "none" };
    }
    case "bot-tts-text":
      return { kind: "bot-tts", text: String(data.text ?? "") };
    case "bot-started-speaking":
      return { kind: "bot-start" };
    case "bot-stopped-speaking":
      return { kind: "bot-pause" };
    case "bot-ready":
      return { kind: "server-ready" };
    default:
      return { kind: "none" };
  }
}

export function buildClientReady() {
  return {
    id: globalThis.crypto?.randomUUID?.() ?? `rtvi-${Date.now()}`,
    label: RTVI_LABEL,
    type: "client-ready",
    data: {
      version: "2.0.0",
      about: { library: "ivin-web", library_version: "1.0.0" },
    },
  };
}
